import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  isLoading: false, data: {}, errors: [], myTalents: [], myTalentsLoading: false,
  current: null, currentLoading: false,
};

export const getTalents = createAsyncThunk('talents/getTalents', async (args, thunkApi) => {
  try {
    const { page = 1, size = 10, query = '', filter = JSON.stringify({}), sort = '' } = args || {};
    const sortParam = sort ? `&sort=${sort}` : '';
    const { data } = await axios.get(
      `/api/get_talents?page=${page}&size=${size}&query=${query}&filter=${filter}${sortParam}`
    );
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const getTalent = createAsyncThunk('talents/getTalent', async (talentId, thunkApi) => {
  try {
    const { data } = await axios.get(`/api/get_talent/${talentId}`);
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const createTalent = createAsyncThunk('talents/createTalent', async (args, thunkApi) => {
  try {
    const { data } = await axios.post('/api/create_talent', args, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const updateTalent = createAsyncThunk('talents/updateTalent', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/update_talent/${args.get ? args.get('_id') : args._id}`, args, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const deleteTalent = createAsyncThunk('talents/deleteTalent', async (args, thunkApi) => {
  try {
    const { data } = await axios.delete(`/api/delete_talent/${args._id}`, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const reactTalent = createAsyncThunk('talents/reactTalent', async ({ _id, action }, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/react_talent/${_id}`, { action }, {
      headers: { Authorization: thunkApi.getState().auth.token },
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.data || { errors: [{ msg: 'something went wrong' }] }
    );
  }
});

export const approveTalent = createAsyncThunk('talents/approveTalent', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/approve_talent/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const disapproveTalent = createAsyncThunk('talents/disapproveTalent', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/disapprove_talent/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const getMyTalents = createAsyncThunk('talents/getMyTalents', async (_, thunkApi) => {
  try {
    const { data } = await axios.get('/api/get_my_talents', {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

const talentsSlice = createSlice({
  name: 'talents',
  initialState,
  extraReducers: {
    [getTalents.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [getTalents.fulfilled]: (state, action) => { state.isLoading = false; state.data = action.payload; },
    [getTalents.rejected]: (state, action) => { state.isLoading = false; state.data = {}; state.errors = action.payload.errors; },

    [getTalent.pending]: (state) => { state.currentLoading = true; state.current = null; },
    [getTalent.fulfilled]: (state, action) => { state.currentLoading = false; state.current = action.payload.data; },
    [getTalent.rejected]: (state) => { state.currentLoading = false; state.current = null; },

    [createTalent.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [createTalent.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.data.itemsCount = (state.data.itemsCount || 0) + 1;
      if (state.data.data?.length < 10) state.data.data = [...(state.data.data || []), action.payload.data];
    },
    [createTalent.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [updateTalent.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [updateTalent.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [updateTalent.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [deleteTalent.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [deleteTalent.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.data.data = state.data.data?.filter(el => el._id !== action.payload.data._id);
      state.data.itemsCount = Math.max(0, (state.data.itemsCount || 1) - 1);
    },
    [deleteTalent.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [reactTalent.fulfilled]: (state, action) => {
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
      if (state.current?._id === action.payload.data._id) state.current = action.payload.data;
    },

    [approveTalent.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [approveTalent.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [approveTalent.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [disapproveTalent.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [disapproveTalent.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [disapproveTalent.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [getMyTalents.pending]: (state) => { state.myTalentsLoading = true; state.myTalents = []; },
    [getMyTalents.fulfilled]: (state, action) => { state.myTalentsLoading = false; state.myTalents = action.payload.data; },
    [getMyTalents.rejected]: (state) => { state.myTalentsLoading = false; },
  }
});

export default talentsSlice.reducer;
